import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { Alert, Box, Button, CircularProgress, Container, Paper, TextField, Typography } from '@mui/material';

const parseHashParams = () => {
  const raw = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
  const params = new URLSearchParams(raw);
  return {
    accessToken: params.get('access_token'),
    refreshToken: params.get('refresh_token'),
    type: params.get('type'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  };
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<'loading' | 'ready' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const urlError = useMemo(() => {
    const err = searchParams.get('error');
    const desc = searchParams.get('error_description');
    if (!err) return null;
    return desc || 'Password reset link is invalid or expired.';
  }, [searchParams]);

  useEffect(() => {
    const init = async () => {
      if (urlError) {
        setStatus('error');
        setMessage(urlError);
        return;
      }

      const hash = parseHashParams();
      if (hash.error) {
        setStatus('error');
        setMessage(hash.errorDescription || 'Password reset link is invalid or expired.');
        return;
      }

      const code = searchParams.get('code');
      const token = searchParams.get('token');
      const type = searchParams.get('type');
      const email = searchParams.get('email');

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (hash.accessToken && hash.refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: hash.accessToken,
            refresh_token: hash.refreshToken,
          });
          if (error) throw error;
        } else if (token && type && email) {
          const { error } = await supabase.auth.verifyOtp({ email, token, type: type as any });
          if (error) throw error;
        }

        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          setStatus('error');
          setMessage('Password reset link is invalid or expired.');
          return;
        }

        setStatus('ready');
        setMessage('');
      } catch (e: any) {
        setStatus('error');
        setMessage(e?.message || 'Failed to initialize password reset.');
      }
    };

    void init();
  }, [searchParams, urlError]);

  const validate = () => {
    if (newPassword.length < 6) return 'Password must be at least 6 characters long.';
    if (newPassword !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setStatus('error');
      setMessage(v);
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setStatus('success');
      setMessage('Password updated successfully. You can now log in.');
      setTimeout(() => navigate('/login'), 900);
    } catch (e: any) {
      setStatus('error');
      setMessage(e?.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Set a new password for your account.
          </Typography>

          {status === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {message && (
            <Alert severity={status === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {status === 'ready' && (
            <form onSubmit={onSubmit}>
              <TextField
                label="New password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={saving}
                required
              />
              <TextField
                label="Confirm password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={saving}
                required
              />
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }} disabled={saving}>
                {saving ? <CircularProgress size={22} /> : 'Update password'}
              </Button>
            </form>
          )}

          {(status === 'error' || status === 'success') && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button variant="text" onClick={() => navigate('/login')}>
                Back to login
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
